import {CommonModule} from '@angular/common';
import {Component, OnInit, inject} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {AdminApiService} from '../../admin-api.service';
import {AuditEvent} from '../../admin.models';
import {AdminTranslatePipe} from '../../core/i18n/admin-translate.pipe';
import {AdminI18nService} from '../../core/i18n/admin-i18n.service';
import {auditActionLabel} from '../../shared/audit-action-labels';
import {UiButtonComponent, UiToastService} from 'freyr-ui';

interface JsonDiffRow {
  path: string;
  before: string | null;
  after: string | null;
  change: 'added' | 'removed' | 'modified' | 'unchanged';
}

@Component({
  selector: 'app-audit-detail-page',
  standalone: true,
  imports: [CommonModule, RouterLink, UiButtonComponent, AdminTranslatePipe],
  templateUrl: './audit-detail-page.component.html',
  styleUrl: './audit-detail-page.component.scss'
})
export class AuditDetailPageComponent implements OnInit {
  private readonly api = inject(AdminApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly i18n = inject(AdminI18nService);
  private readonly toast = inject(UiToastService);
  readonly router = inject(Router);

  loading = false;
  event: AuditEvent | null = null;
  showRawJson = false;

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const auditEventId = params.get('id');
      if (!auditEventId) {
        this.toast.error("Identifiant d'audit manquant.");
        return;
      }
      this.loadAuditEvent(auditEventId);
    });
  }

  prettyJson(value: string | null | undefined): string {
    if (!value) {
      return this.i18n.t('admin.audit.detail.noData');
    }
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }

  actionLabel(actionType: string): string {
    return auditActionLabel(actionType, this.i18n.currentLang);
  }

  get jsonComparisonRows(): JsonDiffRow[] {
    const before = this.parseJson(this.event?.beforeJson);
    const after = this.parseJson(this.event?.afterJson);
    if (before === undefined || after === undefined) {
      return [];
    }

    const beforeMap = this.flattenJson(before);
    const afterMap = this.flattenJson(after);
    const keys = Array.from(new Set([...beforeMap.keys(), ...afterMap.keys()])).sort((left, right) => left.localeCompare(right));

    return keys.map((path): JsonDiffRow => {
      const beforeValue = beforeMap.get(path) ?? null;
      const afterValue = afterMap.get(path) ?? null;

      if (beforeValue === null) {
        return { path, before: null, after: afterValue, change: 'added' };
      }
      if (afterValue === null) {
        return { path, before: beforeValue, after: null, change: 'removed' };
      }
      if (beforeValue !== afterValue) {
        return { path, before: beforeValue, after: afterValue, change: 'modified' };
      }
      return { path, before: beforeValue, after: afterValue, change: 'unchanged' };
    });
  }

  get changedJsonComparisonRows(): JsonDiffRow[] {
    return this.jsonComparisonRows.filter(row => row.change !== 'unchanged');
  }

  get canRenderStructuredComparison(): boolean {
    return this.jsonComparisonRows.length > 0;
  }

  toggleRawJson(): void {
    this.showRawJson = !this.showRawJson;
  }

  private parseJson(value: string | null | undefined): unknown | undefined {
    if (!value?.trim()) {
      return null;
    }
    try {
      return JSON.parse(value);
    } catch {
      return undefined;
    }
  }

  private flattenJson(value: unknown, prefix = ''): Map<string, string> {
    const entries = new Map<string, string>();

    if (value === null || typeof value !== 'object') {
      entries.set(prefix || '$', this.formatJsonValue(value));
      return entries;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        entries.set(prefix || '$', '[]');
        return entries;
      }
      if (this.isDateTimePartsArray(value)) {
        entries.set(prefix || '$', this.formatDateTimeParts(value));
        return entries;
      }
      value.forEach((item, index) => {
        this.flattenJson(item, `${prefix}[${index}]`).forEach((entryValue, entryKey) => entries.set(entryKey, entryValue));
      });
      return entries;
    }

    const objectEntries = Object.entries(value as Record<string, unknown>);
    if (!objectEntries.length) {
      entries.set(prefix || '$', '{}');
      return entries;
    }

    objectEntries.forEach(([key, item]) => {
      const nextPrefix = prefix ? `${prefix}.${key}` : key;
      this.flattenJson(item, nextPrefix).forEach((entryValue, entryKey) => entries.set(entryKey, entryValue));
    });

    return entries;
  }

  private formatJsonValue(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }
    if (value === undefined) {
      return 'undefined';
    }
    return JSON.stringify(value);
  }

  private isDateTimePartsArray(value: unknown[]): value is number[] {
    if (value.length < 3 || value.length > 7) {
      return false;
    }

    if (!value.every(item => typeof item === 'number' && Number.isInteger(item))) {
      return false;
    }

    const [year, month, day, hour = 0, minute = 0, second = 0] = value as number[];
    return year >= 1900
      && year <= 3000
      && month >= 1
      && month <= 12
      && day >= 1
      && day <= 31
      && hour >= 0
      && hour <= 23
      && minute >= 0
      && minute <= 59
      && second >= 0
      && second <= 59;
  }

  private formatDateTimeParts(value: number[]): string {
    const [year, month, day, hour = 0, minute = 0, second = 0] = value;
    const hasTime = value.length > 3;
    const datePart = `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
    if (!hasTime) {
      return datePart;
    }
    return `${datePart} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
  }

  private loadAuditEvent(auditEventId: string): void {
    this.loading = true;
    this.api.getAuditEvent(auditEventId).subscribe({
      next: (event) => {
        this.event = event;
        this.showRawJson = false;
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.loading = false;
        const message = typeof error.error?.message === 'string' && error.error.message.trim()
          ? error.error.message.trim()
          : this.i18n.t('admin.audit.detail.loadError');
        this.toast.error(message);
      }
    });
  }
}

